import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { Restore as RestoreIcon } from '@mui/icons-material';
import type { RevisionDetail } from '../store/hooks';

interface RevertTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  version: number | null;
  revision: RevisionDetail | null;
  isLoading: boolean;
}

/**
 * Formats a date string to a human-readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Extracts a display name from a user ID
 */
function formatCreatedBy(userId: string): string {
  if (userId.includes('@')) {
    return userId;
  }
  if (userId.includes('|')) {
    const [provider] = userId.split('|');
    return provider.replace(/-/g, ' ');
  }
  if (userId.length > 30) {
    return `${userId.slice(0, 27)}...`;
  }
  return userId;
}

export function RevertTemplateModal({
  open,
  onClose,
  onConfirm,
  version,
  revision,
  isLoading,
}: RevertTemplateModalProps) {
  if (!revision || version === null) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon color="primary" />
          Revert to Version {version}?
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          This will create a <strong>new version</strong> with all content from
          version {version}.
        </Typography>

        <Box
          sx={{
            bgcolor: 'grey.50',
            borderRadius: 1,
            p: 2,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Version {version} details:
          </Typography>
          <Typography variant="body2">
            <strong>Saved:</strong> {formatDate(revision.created_at)}
          </Typography>
          <Typography variant="body2">
            <strong>By:</strong> {formatCreatedBy(revision.created_by)}
          </Typography>
          <Typography variant="body2">
            <strong>Sections:</strong> {revision.sections.length}
          </Typography>
          <Typography variant="body2">
            <strong>Title:</strong> {revision.title}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Your current changes (if any) will be discarded. The version history
          will be preserved.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />
          }
        >
          {isLoading ? 'Reverting...' : `Revert to Version ${version}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RevertTemplateModal;
